import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { SupportAttachmentInput } from '@/pages/support/types';
import { uploadBlob } from '@/lib/upload';

const MAX_IMAGES = 3;
const MAX_PDFS = 1;
const MAX_BYTES = 10 * 1024 * 1024;

const isImageFile = (file: { type?: string; contentType?: string }): boolean =>
  (file.type ?? file.contentType ?? '').startsWith('image/');

const isPdfFile = (file: { type?: string; contentType?: string; name?: string; fileName?: string }): boolean => {
  const type = file.type ?? file.contentType ?? '';
  const name = (file.name ?? file.fileName ?? '').toLowerCase();
  return type === 'application/pdf' || name.endsWith('.pdf');
};

export type PendingSupportUpload = {
  name: string;
  size: string;
  contentType: string;
  previewUrl: string | null;
};

export const useSupportTicketUploads = () => {
  const { t } = useTranslation('support');
  const [attachments, setAttachments] = useState<SupportAttachmentInput[]>([]);
  const [pending, setPending] = useState<PendingSupportUpload[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<number | undefined>();
  const [error, setError] = useState<string | undefined>();

  const reset = useCallback(() => {
    setPending((current) => {
      for (const file of current) {
        if (file.previewUrl) URL.revokeObjectURL(file.previewUrl);
      }
      return [];
    });
    setAttachments([]);
    setUploading(false);
    setProgress(undefined);
    setError(undefined);
  }, []);

  const addFiles = useCallback(
    async (files: File[]) => {
      setError(undefined);
      let images = attachments.filter((file) => isImageFile(file)).length;
      let pdfs = attachments.filter((file) => isPdfFile(file)).length;
      const batch: File[] = [];
      for (const file of files) {
        if (file.size > MAX_BYTES) {
          setError(t('attachments.tooLarge'));
          return;
        }
        if (isImageFile(file)) {
          if (images >= MAX_IMAGES) {
            setError(t('attachments.tooManyImages', { max: MAX_IMAGES }));
            continue;
          }
          images += 1;
          batch.push(file);
          continue;
        }
        if (isPdfFile(file)) {
          if (pdfs >= MAX_PDFS) {
            setError(t('attachments.tooManyPdfs'));
            continue;
          }
          pdfs += 1;
          batch.push(file);
          continue;
        }
        setError(t('attachments.invalidType'));
        return;
      }
      if (batch.length === 0) return;

      setUploading(true);
      setProgress(0);
      try {
        const next = [...attachments];
        const nextPending = [...pending];
        for (const [index, file] of batch.entries()) {
          setProgress(Math.round(((index + 0.5) / batch.length) * 100));
          const uploaded = await uploadBlob({
            purpose: 'support_ticket_file',
            blob: file,
            fileName: file.name,
          });
          next.push({
            fileKey: uploaded.key,
            fileName: file.name,
            contentType: uploaded.contentType,
            byteSize: file.size,
          });
          nextPending.push({
            name: file.name,
            size: t('attachments.fileSize', { size: formatBytes(file.size) }),
            contentType: uploaded.contentType || file.type,
            previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
          });
        }
        setAttachments(next);
        setPending(nextPending);
        setProgress(100);
      } catch {
        setError(t('attachments.uploadFailed'));
      } finally {
        setUploading(false);
        setTimeout(() => setProgress(undefined), 400);
      }
    },
    [attachments, pending, t],
  );

  const removeAt = useCallback((index: number) => {
    setPending((prev) => {
      const removed = prev[index];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
    setAttachments((prev) => prev.filter((_, i) => i !== index));
    setError(undefined);
  }, []);

  return {
    attachments,
    pending,
    uploading,
    progress,
    error,
    addFiles,
    removeAt,
    reset,
    maxImages: MAX_IMAGES,
    maxPdfs: MAX_PDFS,
    maxBytes: MAX_BYTES,
    atLimit:
      attachments.filter((file) => isImageFile(file)).length >= MAX_IMAGES &&
      attachments.filter((file) => isPdfFile(file)).length >= MAX_PDFS,
  };
};

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
